import React, { Fragment } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Loader } from "../Loader";
import { cn } from "@/lib/utils";
import { RecursiveSubNRowsProps } from "./types";

const RecursiveSubRows: React.FC<RecursiveSubNRowsProps> = ({ 
  childrenData,
  parentId,
  depth, //depth starts from 0
  nestedColumns,
  expandedCellClassName,
  renderExpandedContent,
  icons,
  hideHeader,
  isSecondary,
  tableMeta,
  visibleColCount,
}) => {
  // Get columns for the current level. If level index exceeds array, fallback to the last available column set.
  const currentColumns = nestedColumns[depth] || nestedColumns[nestedColumns.length - 1] || [];
  const shouldHideHeader = hideHeader?.[depth] ?? hideHeader?.[`level${depth + 1}`];
  const currentIcon = icons?.[depth] ?? icons?.[`level${depth + 1}`];

  if (shouldHideHeader) {
    return (
      // <TableRow className={cn(isSecondary ? "" : "bg-blue-50/50 hover:bg-blue-50/50")}>
      //   <TableCell
      //     colSpan={visibleColCount}
      //     className={cn("p-2 md:p-4", expandedCellClassName)}
      //   >
      <Fragment>
        {childrenData?.length ? (
          childrenData.map((child: any, idx: number) => {
            const childRowId = `${parentId}_child_${idx}`;
            const hasNestedChildren = !!(child?.children?.length && Array.isArray(child.children));
            const isExpanded = tableMeta.isChildExpanded(childRowId);
  
            return (
              <React.Fragment key={childRowId}>
                {/* Sub-row */}
                <TableRow className={cn(isSecondary ? (isExpanded ? "border-b-0" : "border-b") : "", depth % 2 == 0 ? "bg-slate-100 hover:bg-slate-100" : "")}>
                  {currentColumns.map((col: any, colIdx: number) => {
                    const size = col.size || "auto";
  
                    return (
                      <TableCell
                        key={col.accessorKey || col.id || colIdx}
                        className={cn(
                          col?.meta?.align === "right" ? "text-right" : "text-left",
                          "text-gray-600 font-normal text-xs md:text-sm p-2.5"
                        )}
                      >
                        <div
                          className={cn(
                            "flex items-center gap-2",
                            col?.meta?.align === "right" ? "justify-end" : ""
                          )}
                          style={{ width: size, paddingLeft: colIdx === 0 ? `${(depth + 1) * 10}px` : "auto" }}
                        >
                          {/* Expand Button for Level N */}
                          {colIdx === 0 && hasNestedChildren && (
                            <button
                              type="button"
                              onClick={() => tableMeta.toggleChildExpanded(childRowId)}
                              className="flex items-center justify-center border border-primary text-primary rounded-full p-0.5 hover:bg-primary/10 transition-colors"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              )}
                            </button>
                          )}
  
                          {/* Level Icon */}
                          {colIdx === 0 && (currentIcon || null)}
  
                          {/* Cell Content */}
                          <span className="flex text-wrap">
                            {col.accessorFn
                              ? col.accessorFn(child)
                              : col.cell
                              ? col.cell(child)
                              : child[col.accessorKey]}
                          </span>
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
  
                {/* Custom Content Override OR Recursive Deep Expansion */}
                {isExpanded && renderExpandedContent && (
                  <TableRow>
                    <TableCell colSpan={currentColumns.length}>
                      {renderExpandedContent(child, depth + 1)}
                    </TableCell>
                  </TableRow>
                )}
  
                {isExpanded && !renderExpandedContent && hasNestedChildren && (
                  <RecursiveSubRows
                    childrenData={child.children}
                    parentId={childRowId}
                    depth={depth + 1}
                    nestedColumns={nestedColumns}
                    expandedCellClassName={expandedCellClassName}
                    renderExpandedContent={renderExpandedContent}
                    icons={icons}
                    hideHeader={hideHeader}
                    isSecondary={isSecondary}
                    tableMeta={tableMeta}
                    visibleColCount={currentColumns.length}
                  />
                )}
              </React.Fragment>
            );
          })
        ) : (
          <Loader />
        )}
      </Fragment>
      //   </TableCell>
      // </TableRow>
    );
  } else {
    return (
      <TableRow className={cn(isSecondary ? "" : "bg-blue-50/50 hover:bg-blue-50/50")}>
        <TableCell
          colSpan={visibleColCount}
          className={cn("p-2 md:p-4", expandedCellClassName)}
        >
          <Fragment>
            {childrenData?.length ? (
              childrenData.map((child: any, idx: number) => {
                const childRowId = `${parentId}_child_${idx}`;
                const hasNestedChildren = !!(child?.children?.length && Array.isArray(child.children));
                const isExpanded = tableMeta.isChildExpanded(childRowId);
      
                return (
                  <React.Fragment key={childRowId}>
                    {/* Sub-row */}
                    <TableRow className={cn(isSecondary ? (isExpanded ? "border-b-0" : "border-b") : "", depth % 2 == 0 ? "bg-slate-100 hover:bg-slate-100" : "")}>
                      {currentColumns.map((col: any, colIdx: number) => {
                        const size = col.size || "auto";
      
                        return (
                          <TableCell
                            key={col.accessorKey || col.id || colIdx}
                            className={cn(
                              col?.meta?.align === "right" ? "text-right" : "text-left",
                              "text-gray-600 font-normal text-xs md:text-sm p-2.5"
                            )}
                          >
                            <div
                              className={cn(
                                "flex items-center gap-2",
                                col?.meta?.align === "right" ? "justify-end" : ""
                              )}
                              style={{ width: size, paddingLeft: colIdx === 0 ? `${(depth + 1) * 10}px` : "auto" }}
                            >
                              {/* Expand Button for Level N */}
                              {colIdx === 0 && hasNestedChildren && (
                                <button
                                  type="button"
                                  onClick={() => tableMeta.toggleChildExpanded(childRowId)}
                                  className="flex items-center justify-center border border-primary text-primary rounded-full p-0.5 hover:bg-primary/10 transition-colors"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-3.5 h-3.5" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
      
                              {/* Level Icon */}
                              {colIdx === 0 && (currentIcon || null)}
      
                              {/* Cell Content */}
                              <span className="flex text-wrap">
                                {col.accessorFn
                                  ? col.accessorFn(child)
                                  : col.cell
                                  ? col.cell(child)
                                  : child[col.accessorKey]}
                              </span>
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
      
                    {/* Custom Content Override OR Recursive Deep Expansion */}
                    {isExpanded && renderExpandedContent && (
                      <TableRow>
                        <TableCell colSpan={currentColumns.length}>
                          {renderExpandedContent(child, depth + 1)}
                        </TableCell>
                      </TableRow>
                    )}
      
                    {isExpanded && !renderExpandedContent && hasNestedChildren && (
                      <RecursiveSubRows
                        childrenData={child.children}
                        parentId={childRowId}
                        depth={depth + 1}
                        nestedColumns={nestedColumns}
                        expandedCellClassName={expandedCellClassName}
                        renderExpandedContent={renderExpandedContent}
                        icons={icons}
                        hideHeader={hideHeader}
                        isSecondary={isSecondary}
                        tableMeta={tableMeta}
                        visibleColCount={currentColumns.length}
                      />
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <Loader />
            )}
          </Fragment>
        </TableCell>
      </TableRow>
    );
  }
};

export default RecursiveSubRows;
